import {
    useEffect,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IMenubarTriggerProps from "./type";
import type {
    MenubarTriggerWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useMenubarMenuContext,
    useMenubarContext
} from "../../context";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import type {
    MenubarKeyboardEventType
} from "../../type";
import {
    getIsDropdownMenuKeyboardFocus,
    DROPDOWN_MENU_ARROW_DOWN_KEY,
    DROPDOWN_MENU_ARROW_UP_KEY
} from "../../../dropdownMenu/util";
import {
    MENUBAR_ESCAPE_KEY,
    MENUBAR_SPACE_KEY,
    MENUBAR_HOME_KEY,
    MENUBAR_END_KEY
} from "../../util";
import {
    getControlMeasures
} from "../../../../helpers/theme/control";
import {
    getMenubarBarPad
} from "../../stylesheet";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const MenubarTrigger: FC<IMenubarTriggerProps> = ({
    customTheme: customThemeProp,
    isDisabled = false,
    testID,
    style,
    title
}) => {
    const context = useMenubarContext({
        part: "Trigger"
    });

    const menuContext = useMenubarMenuContext({
        part: "Trigger"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const handleRegister = context?.handleRegister;

    const handleUnregister = context?.handleUnregister;

    const handleTriggerFocus = context?.handleTriggerFocus;

    const menuKey = menuContext?.menuKey;

    const triggerRef = menuContext?.triggerRef;

    const isOpen = menuContext?.isOpen === true;

    const isOtherOpen = context?.openKey !== null && !isOpen;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverIn: () => {
            if(!context || !menuKey || isDisabled || !isOtherOpen) {
                return;
            }

            context.handleOpenChange(menuKey, "pointer");
        },
        onFocus: () => {
            if(menuKey) {
                handleTriggerFocus?.(menuKey);
            }
        }
    });

    const currentSize = getControlMeasures({
        controlPaddings,
        controlHeights,
        size: "small",
        shapeConfigs,
        iconSizes
    });

    const currentColors = getTypeColors({
        variant: "ghost",
        type: "neutral",
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        anchor: anchorDynamicStyle
    } = useStyles({
        barPad: getMenubarBarPad({
            controlHeights
        }),
        isFocusVisible,
        currentColors,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        isDisabled,
        radiuses,
        borders,
        colors
    });

    useEffect(() => {
        if(!menuKey || !handleRegister || !handleUnregister) {
            return;
        }

        handleRegister(menuKey, {
            node: triggerRef?.current ?? null,
            isDisabled
        });

        return () => {
            handleUnregister(menuKey);
        };
    }, [
        handleUnregister,
        handleRegister,
        isDisabled,
        triggerRef,
        menuKey
    ]);

    if(!context || !menuContext) {
        return null;
    }

    const handlePress = () => {
        if(isDisabled) {
            return;
        }

        if(isOpen) {
            context.handleOpenChange(null);

            return;
        }

        context.handleOpenChange(menuContext.menuKey, getIsDropdownMenuKeyboardFocus(menuContext.triggerRef.current) ? "first" : "pointer");
    };

    const onKeyDown = (event: MenubarKeyboardEventType) => {
        if(event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === MENUBAR_SPACE_KEY) {
            event.preventDefault();

            return;
        }

        if(event.key === DROPDOWN_MENU_ARROW_DOWN_KEY || event.key === DROPDOWN_MENU_ARROW_UP_KEY) {
            event.preventDefault();

            if(!isDisabled) {
                context.handleOpenChange(menuContext.menuKey, event.key === DROPDOWN_MENU_ARROW_DOWN_KEY ? "first" : "last");
            }

            return;
        }

        if(event.key === MENUBAR_HOME_KEY || event.key === MENUBAR_END_KEY) {
            event.preventDefault();

            context.handleMove(menuContext.menuKey, event.key === MENUBAR_HOME_KEY ? "first" : "last");

            return;
        }

        if(event.key !== MENUBAR_ESCAPE_KEY) {
            return;
        }

        event.preventDefault();

        if(isOpen) {
            context.handleClose(menuContext.menuKey);

            return;
        }

        context.handleRestorePrevious();
    };

    const onKeyUp = (event: MenubarKeyboardEventType) => {
        if(event.key !== MENUBAR_SPACE_KEY || event.altKey || event.ctrlKey || event.metaKey || isDisabled) {
            return;
        }

        event.preventDefault();

        handlePress();
    };

    const webProps: MenubarTriggerWebPropsType = Platform.OS === "web" ? {
        onKeyDown,
        onKeyUp
    } : {};

    const isTabStop = (context.openKey ?? context.tabKey) === menuContext.menuKey;

    return <View
        ref={menuContext.anchorRef}
        style={[
            stylesheet.anchor,
            anchorDynamicStyle
        ]}
        collapsable={false}
    >
        <Pressable
            {...webProps}
            {...interactionHandlers}
            aria-controls={isOpen ? menuContext.contentId : undefined}
            tabIndex={isTabStop ? 0 : -1}
            ref={menuContext.triggerRef}
            aria-disabled={isDisabled}
            id={menuContext.triggerId}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            aria-expanded={isOpen}
            disabled={isDisabled}
            onPress={handlePress}
            aria-haspopup="menu"
            testID={testID}
            role="menuitem"
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    isPressed={!isDisabled && (isPressed || isOpen)}
                    isHovered={!isDisabled && isHovered && !isOpen}
                    overlayColor={currentColors.overlayColor}
                    borderRadius={radiuses.actions}
                    borderWidth={borders.line}
                    customTheme={customTheme}
                />
                <Text
                    customColor={currentColors.titleColor}
                    variant={currentSize.labelVariant}
                    customTheme={customTheme}
                    style={stylesheet.title}
                    numberOfLines={1}
                >
                    {title}
                </Text>
            </View>
        </Pressable>
    </View>;
};
export default MenubarTrigger;
